import React, { useState, useEffect } from 'react';
import { 
  FileText, Eye, Download, Trash2, X, 
  FolderOpen, Home, User, Calendar, Search,
  Edit3
} from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { useToast } from '../../components/ui/ToastProvider';
import { Document as PdfDocument, Page, pdfjs } from 'react-pdf';
import { auth, db } from '../../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { H1, H3 } from '../../components/typography';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const CATEGORIES = {
  hyresavtal: { name: 'Hyresavtal', icon: FileText, color: 'text-[#62B8E9]' },
  medlemsforteckning: { name: 'Medlemsförteckning', icon: User, color: 'text-[#1CBA19]' },
  forsakringsbrev: { name: 'Försäkringsbrev', icon: FileText, color: 'text-[#62B8E9]' },
  ovriga: { name: 'Övriga dokument', icon: FileText, color: 'text-[#2C2C2C]' }
};

// Mock data med den nya strukturen
const initialDocs = [
  {
    id: 1,
    name: 'Hyresavtal_1101_Anna_Andersson.pdf',
    originalName: 'hyresavtal.pdf',
    category: 'hyresavtal',
    ownerId: 'owner1',
    ownerName: 'Anna Andersson',
    apartmentNumber: '1101',
    uploadDate: '2024-01-15',
    fileSize: '245KB',
    fileObject: null
  },
  {
    id: 2,
    name: 'Hyresavtal_1102_Erik_Svensson.pdf',
    originalName: 'kontrakt.pdf',
    category: 'hyresavtal',
    ownerId: 'owner2', 
    ownerName: 'Erik Svensson',
    apartmentNumber: '1102',
    uploadDate: '2024-01-20',
    fileSize: '312KB',
    fileObject: null
  },
  {
    id: 3,
    name: 'Medlemsförteckning_2024_Januari.xlsx',
    originalName: 'medlemmar.xlsx',
    category: 'medlemsforteckning',
    ownerId: null,
    ownerName: null,
    apartmentNumber: null,
    uploadDate: '2024-01-31',
    fileSize: '89KB',
    fileObject: null
  },
  {
    id: 4,
    name: 'Hemforsakring_1101_Anna_Andersson.pdf',
    originalName: 'försäkring.pdf', 
    category: 'forsakringsbrev',
    ownerId: 'owner1',
    ownerName: 'Anna Andersson',
    apartmentNumber: '1101',
    uploadDate: '2024-02-01',
    fileSize: '156KB',
    fileObject: null
  },
  {
    id: 5,
    name: 'Ansvarsforsakring_1102_Erik_Svensson.pdf',
    originalName: 'ansvarsförsäkring.pdf', 
    category: 'forsakringsbrev',
    ownerId: 'owner2',
    ownerName: 'Erik Svensson',
    apartmentNumber: '1102',
    uploadDate: '2024-02-05',
    fileSize: '203KB',
    fileObject: null
  }
];

export default function Documents() {
  const [docs, setDocs] = useState(initialDocs);
  const [selectedCategory, setSelectedCategory] = useState('hyresavtal');
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOwner, setSelectedOwner] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [availableOwners, setAvailableOwners] = useState([]);
  
  // Modal states
  const [previewDoc, setPreviewDoc] = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentBrf, setCurrentBrf] = useState('');
  
  const toast = useToast();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchOwnersForUser(user.uid);
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    filterDocuments();
  }, [docs, selectedCategory, searchQuery, selectedOwner, selectedYear]);

  async function fetchOwnersForUser(uid) {
    setLoading(true);
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return;

      const userData = userSnap.data();
      const associationId = userData.associationId;
      const housingCooperative = userData.housingCooperative;
      
      setCurrentBrf(housingCooperative);
      
      if (!associationId) return;
      
      // Hämta alla lägenheter för föreningen
      const q = query(
        collection(db, 'housing'),
        where('associationId', '==', associationId)
      );
      const querySnapshot = await getDocs(q);
      
      const owners = [];
      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        if (data.ownedBy) {
          // Hämta ägarens namn
          try {
            const ownerRef = doc(db, 'users', data.ownedBy);
            const ownerSnap = await getDoc(ownerRef);
            if (ownerSnap.exists()) {
              const ownerData = ownerSnap.data();
              owners.push({
                id: data.ownedBy,
                name: `${ownerData.firstName || ''} ${ownerData.lastName || ''}`.trim(),
                apartmentNumber: data.apartmentNumber,
                email: ownerData.email
              });
            }
          } catch (err) {
          }
        }
      }
      
      setAvailableOwners(owners);
    } catch (err) {
      toast({ type: 'error', message: 'Kunde inte hämta lägenhetsinformation' });
    } finally {
      setLoading(false);
    }
  }

  const filterDocuments = () => {
    let filtered = docs.filter(doc => doc.category === selectedCategory);
    
    if (searchQuery) {
      filtered = filtered.filter(doc => 
        doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.ownerName && doc.ownerName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (doc.apartmentNumber && doc.apartmentNumber.includes(searchQuery))
      );
    }
    
    if (selectedOwner) {
      filtered = filtered.filter(doc => doc.ownerId === selectedOwner);
    }
    
    if (selectedYear) {
      filtered = filtered.filter(doc => doc.uploadDate.startsWith(selectedYear));
    }
    
    setFilteredDocs(filtered);
  };



  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleDelete = () => {
    setDocs(prev => prev.filter(d => d.id !== deleteDoc.id));
    setDeleteDoc(null);
    toast({ type: 'success', message: 'Dokument borttaget.' });
  };

  const getCategoryIcon = (category) => {
    const CategoryIcon = CATEGORIES[category]?.icon || FileText;
    return <CategoryIcon className={`w-5 h-5 ${CATEGORIES[category]?.color || 'text-gray-600'}`} />;
  };

  const getCategoryCount = (category) => {
    return docs.filter(doc => doc.category === category).length;
  };

  const getAvailableYears = () => {
    const years = [...new Set(docs.map(doc => doc.uploadDate.slice(0, 4)))];
    return years.sort().reverse();
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-gray-200 rounded mb-6"></div>
          <div className="flex gap-6">
            <div className="w-64 h-96 bg-gray-200 rounded"></div>
            <div className="flex-1 h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8">
      <H1>Dokumenthantering</H1>

      <div className="flex gap-6">
        {/* Sidopanel med mappsystem och filter */}
        <div className="w-64 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          {/* Mappsystem */}
          <div className="mb-6">
            <H3 className="uppercase tracking-wide">Mappsystem</H3>
            <div className="space-y-1">
              {Object.entries(CATEGORIES).map(([key, category]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors ${
                    selectedCategory === key
                      ? 'bg-[#e6f3fa] text-[#62B8E9] border border-[#62B8E9]'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(key)}
                    <span className="text-sm font-medium">{category.name}</span>
                  </div>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    {getCategoryCount(key)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Filter */}
          <div className="space-y-4">
            <H3 className="uppercase tracking-wide">Filtrera</H3>
            
            {/* Sök */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Sök</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
        <input
                  type="text"
                  placeholder="Sök dokument..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]/20"
        />
      </div>
            </div>

            {/* Lägenhet/Ägare (för hyresavtal och försäkringsbrev) */}
            {(selectedCategory === 'hyresavtal' || selectedCategory === 'forsakringsbrev') && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Ägare</label>
                <select
                  value={selectedOwner}
                  onChange={(e) => setSelectedOwner(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]/20"
                >
                  <option value="">Alla ägare</option>
                  {availableOwners.map(owner => (
                    <option key={owner.id} value={owner.id}>
                      {owner.apartmentNumber} - {owner.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* År */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">År</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]/20"
              >
                <option value="">Alla år</option>
                {getAvailableYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Huvudområde */}
        <div className="flex-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getCategoryIcon(selectedCategory)}
                  <h2 className="text-lg font-semibold text-gray-900">
                    {CATEGORIES[selectedCategory]?.name} ({filteredDocs.length} dokument)
                  </h2>
                </div>
              </div>
            </div>

            {/* Dokumentlista */}
            <div className="p-4">
              {filteredDocs.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Inga dokument</h3>
                                     <p className="text-gray-500">
                     {searchQuery || selectedOwner || selectedYear
                       ? 'Inga dokument matchar dina filterkriterier.'
                       : `Inga dokument i kategorin ${CATEGORIES[selectedCategory]?.name.toLowerCase()}.`}
                   </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredDocs.map(doc => (
                    <div
                      key={doc.id}
                      className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[#62B8E9]" />
                            <div>
                              <h3 className="font-medium text-gray-900">{doc.name}</h3>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                {doc.ownerName && (
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {doc.ownerName}
                                  </span>
                                )}
                                {doc.apartmentNumber && (
                                  <span className="flex items-center gap-1">
                                    <Home className="w-3 h-3" />
                                    Lgh {doc.apartmentNumber}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {doc.uploadDate}
                                </span>
                                <span>{doc.fileSize}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPreviewDoc(doc)}
                            className="p-2 rounded-full hover:bg-[#e6f3fa] transition text-[#62B8E9]"
                            title="Förhandsgranska"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                            onClick={() => toast({ type: 'info', message: 'Nedladdning (mock)' })}
                            className="p-2 rounded-full hover:bg-[#e6f3fa] transition text-[#62B8E9]"
                    title="Ladda ner"
                  >
                    <Download className="w-4 h-4" />
                  </button>

                          <button
                            onClick={() => setDeleteDoc(doc)}
                            className="p-2 rounded-full hover:bg-red-50 transition text-red-600"
                    title="Ta bort"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                        </div>
                      </div>
                    </div>
            ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      

      {/* Preview Modal */}
      <Dialog open={!!previewDoc} onClose={() => setPreviewDoc(null)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-xl font-bold text-gray-900">
                Förhandsgranskning
              </Dialog.Title>
          <button
            onClick={() => setPreviewDoc(null)}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
            </div>

          {previewDoc?.fileObject ? (
            <div className="flex flex-col items-center">
                <PdfDocument 
                  file={previewDoc.fileObject} 
                  loading={<div className="py-8">Laddar PDF...</div>}
                  error={<div className="py-8 text-red-500">Kunde inte ladda PDF</div>}
                >
                  <Page pageNumber={1} width={Math.min(800, window.innerWidth - 100)} />
              </PdfDocument>
                <p className="mt-4 text-sm text-gray-500">{previewDoc.name}</p>
            </div>
          ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <FileText className="w-16 h-16 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{previewDoc?.name}</h3>
                <p className="text-sm">Förhandsgranskning ej tillgänglig</p>
            </div>
          )}
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteDoc} onClose={() => setDeleteDoc(null)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <Dialog.Title className="text-lg font-medium text-gray-900 mb-2">
                Ta bort dokument?
              </Dialog.Title>
              <p className="text-sm text-gray-500 mb-6">
                Vill du verkligen ta bort <strong>{deleteDoc?.name}</strong>? 
                Denna åtgärd kan inte ångras.
              </p>
              <div className="flex gap-3">
          <button
            onClick={() => setDeleteDoc(null)}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
                  Avbryt
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
                >
                  Ta bort
          </button>
              </div>
          </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
} 